import React, { useCallback } from 'react';
import { View, Text, TouchableOpacity, Platform, Dimensions } from 'react-native';
import type { CalendarViewMode } from '../types';
import type { CalendarTheme } from '../types';
import { styles } from '../styles';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isIOSMobile = Platform.OS === 'ios' && SCREEN_WIDTH < 768;

interface ControlBarProps {
  activeViewMode: CalendarViewMode;
  currentDate: Date;
  onViewModeChange: (mode: CalendarViewMode) => void;
  onPreviousClick: () => void;
  onNextClick: () => void;
  theme: CalendarTheme;
  getWeekDates: (date: Date) => Date[];
}

/**
 * ControlBar Component
 * Displays view mode switcher (Weekly/Monthly) and navigation controls
 */
export const ControlBar: React.FC<ControlBarProps> = ({
  activeViewMode,
  currentDate,
  onViewModeChange,
  onPreviousClick,
  onNextClick,
  theme,
  getWeekDates,
}) => {
  const handleViewModeChange = useCallback(
    (mode: CalendarViewMode) => {
      onViewModeChange(mode);
    },
    [onViewModeChange]
  );

  return (
    <View style={styles.controlBar}>
      {/* Left Section - View Mode Switcher */}
      <View
        style={[
          styles.viewSwitcher,
          {
            backgroundColor: theme.controlBackgroundColor,
            borderColor: theme.controlBorderColor,
            borderWidth: 1,
          },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.viewButton,
            activeViewMode === 'weekly' && styles.viewButtonActive,
            activeViewMode === 'weekly' && {
              backgroundColor: theme.controlActiveBackgroundColor,
              borderColor: theme.controlActiveBackgroundColor,
            },
          ]}
          onPress={() => handleViewModeChange('weekly')}
        >
          <Text
            style={[
              styles.viewButtonText,
              activeViewMode === 'weekly' && styles.viewButtonTextActive,
              {
                color:
                  activeViewMode === 'weekly'
                    ? theme.controlActiveTextColor
                    : theme.controlTextColor,
              },
            ]}
          >
            Weekly
          </Text>
        </TouchableOpacity>
        {/* Hide Monthly tab on iOS mobile */}
        {!isIOSMobile && (
          <TouchableOpacity
            style={[
              styles.viewButton,
              activeViewMode === 'monthly' && styles.viewButtonActive,
              activeViewMode === 'monthly' && {
                backgroundColor: theme.controlActiveBackgroundColor,
                borderColor: theme.controlActiveBackgroundColor,
              },
            ]}
            onPress={() => handleViewModeChange('monthly')}
          >
            <Text
              style={[
                styles.viewButtonText,
                activeViewMode === 'monthly' && styles.viewButtonTextActive,
                {
                  color:
                    activeViewMode === 'monthly'
                      ? theme.controlActiveTextColor
                      : theme.controlTextColor,
                },
              ]}
            >
              Monthly
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Right Section - Navigation Controls and Month/Year */}
      <View style={styles.rightControlSection}>
        <View style={styles.navigationContainer}>
          <TouchableOpacity
            onPress={onPreviousClick}
            style={styles.navChevron}
          >
            <Text style={[styles.chevronText, { color: theme.chevronColor }]}>‹</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onNextClick}
            style={styles.navChevron}
          >
            <Text style={[styles.chevronText, { color: theme.chevronColor }]}>›</Text>
          </TouchableOpacity>
        </View>

        {/* Month/Year Text */}
        <Text style={[styles.monthYearText, { color: theme.headerTextColor }]}>
          {activeViewMode === 'weekly'
            ? (() => {
                const dates = getWeekDates(currentDate);
                return `${dates[0]?.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })} - ${dates[6]?.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}`;
              })()
            : currentDate.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
        </Text>
      </View>
    </View>
  );
};

export default ControlBar;
